import { components } from "@/api-schema";
import { RelocationOrderStatus } from "@/api-schema-enums";
import { RowAction, RowActionVariant } from "@/components/ui/table-row-actions";
import { CheckIcon } from "lucide-react";
import { RelocationOrderActionsContext } from "@/common/hooks/relocation-order/_helpers/relocation-order-actions-context";
import { buildDestinationBranchActions } from "./internal/destination-branch/relocation-order-destination-branch-actions";
import { buildManagingBranchByOwnAxleActions } from "./internal/origin-branch/relocation-order-managing-branch-by-own-axle-actions";
import { buildManagingBranchByTransportCompanyActions } from "./internal/origin-branch/relocation-order-managing-branch-by-transport-company-actions";
import { buildOriginBranchByOwnAxleActions } from "./internal/origin-branch/relocation-order-origin-branch-by-own-axle-actions";
import { buildOriginBranchByTransportCompanyActions } from "./internal/origin-branch/relocation-order-origin-branch-by-transport-company-actions";

type RelocationOrder = components["schemas"]["RelocationOrder"];

export interface OrderVariantRoles {
  isOriginBranch: boolean;
  isDestinationBranch: boolean;
}

export function buildB2BTransportCompanyActions(
  order: RelocationOrder,
  roles: OrderVariantRoles,
  context: RelocationOrderActionsContext,
): RowAction[] {
  const actions: RowAction[] = [];

  if (roles.isOriginBranch) actions.push(...buildOriginBranchByTransportCompanyActions(order, context));
  if (roles.isDestinationBranch) actions.push(...buildDestinationBranchActions(order, context));

  return actions;
}

export function buildB2BOwnAxleActions(
  order: RelocationOrder,
  roles: OrderVariantRoles,
  context: RelocationOrderActionsContext,
): RowAction[] {
  const actions: RowAction[] = [];

  if (roles.isOriginBranch) actions.push(...buildOriginBranchByOwnAxleActions(order, context));
  if (roles.isDestinationBranch) actions.push(...buildDestinationBranchActions(order, context));

  return actions;
}

export function buildB2ETransportCompanyActions(
  order: RelocationOrder,
  roles: OrderVariantRoles,
  context: RelocationOrderActionsContext,
): RowAction[] {
  if (!roles.isOriginBranch) return [];

  return buildOriginBranchByTransportCompanyActions(order, context);
}

export function buildB2EOwnAxleActions(
  order: RelocationOrder,
  roles: OrderVariantRoles,
  context: RelocationOrderActionsContext,
): RowAction[] {
  if (!roles.isOriginBranch) return [];

  const actions = buildOriginBranchByOwnAxleActions(order, context);

  if (order.status === RelocationOrderStatus.TransportCompanyAwaitingDeliveryAcceptance) {
    actions.push({
      label: context.t("fit:CompleteOrder"),
      icon: <CheckIcon className="h-4 w-4" />,
      variant: RowActionVariant.Success,
      needsConfirm: true,
      confirmLabels: {
        title: context.t("fit:CompleteOrder"),
        text: context.t("fit:DoYouWantToCompleteThisOrder"),
        confirmButton: context.t("common:Confirm"),
      },
      onClick: () => context.handleCompleteOrder(order),
    });
  }

  return actions;
}

export function buildE2BTransportCompanyActions(
  order: RelocationOrder,
  roles: OrderVariantRoles,
  context: RelocationOrderActionsContext,
): RowAction[] {
  if (!roles.isDestinationBranch) return [];

  return [
    // For this case the destination branch gets the managing actions
    // since there is no origin branch involvement
    ...buildManagingBranchByTransportCompanyActions(order, context),
    ...buildDestinationBranchActions(order, context),
  ];
}

export function buildE2BOwnAxleActions(
  order: RelocationOrder,
  roles: OrderVariantRoles,
  context: RelocationOrderActionsContext,
): RowAction[] {
  if (!roles.isDestinationBranch) return [];

  return [
    // For this case the destination branch gets the managing actions
    // since there is no origin branch involvement
    ...buildManagingBranchByOwnAxleActions(order, context),
    ...buildDestinationBranchActions(order, context),
  ];
}
